import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  Outlet,
} from 'react-router-dom';
import {
  useQueryClient,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';

import { GlobalProvider, UserPreferencesProvider } from '@contexts';
import {
  AddAuthor,
  AddBlogCategory,
  AddContentDatabaseItem,
  AddDataModel,
  AddMenu,
  SiteAddMenu,
  AddPage,
  AddPost,
  AddProject,
  AddTemplate,
  AddUser,
  AddWebsite,
  AuditLog,
  Author,
  Authors,
  BlogCategories,
  BlogCategory,
  Blogs,
  BulkEdit,
  AddGroup,
  FormLeads,
  ContentDatabaseItem,
  ContentDatabaseItems,
  DataModel,
  DataModels,
  Location,
  LocationDetails,
  LocationSettings,
  LocationSocial,
  Locations,
  Organization,
  Pages,
  Post,
  Project,
  Projects,
  GroupDetails,
  ProjectSettings,
  Search,
  SiteAccess,
  SiteLocationDetails,
  SiteLocationSocial,
  SiteManageContent,
  SitePage,
  SiteSettings,
  Template,
  TemplateAddPage,
  TemplateAddPost,
  TemplateBlogs,
  TemplateEditor,
  TemplateFallbacks,
  TemplateMenu,
  TemplateMenus,
  TemplatePage,
  TemplatePages,
  TemplatePost,
  TemplateSettings,
  Templates,
  User,
  Users,
  Websites,
  YourAccount,
  Groups,
  InterstitialPage,
  SiteMenus,
  SiteMenu,
  SEOPage,
} from '@routes';

import './index.scss';
import './forms.scss';

if (import.meta.env.PROD) {
  // sentry error logging, only in production
  Sentry.init({
    dsn: 'https://c493056e2b93720eeb29bff01f14b0eb@o6833.ingest.us.sentry.io/4507085978337280',
    environment: import.meta.env.MODE,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    // Performance Monitoring
    tracesSampleRate: 1.0, //  Capture 100% of the transactions
    // Session Replay
    replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
    replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  });
}

const queryClient = new QueryClient();

const router = createBrowserRouter([
  {
    path: '/',
    Component() {
      // wrap all childen with our global context provider to handle
      // auth and redirects to Django login page when needed
      // also wrap our user preferences provider
      return (
        <GlobalProvider>
          <UserPreferencesProvider>
            <Outlet />
          </UserPreferencesProvider>
        </GlobalProvider>
      );
    },
    children: [
      {
        path: '/dashboard/',
        element: <Navigate to="/dashboard/projects" replace />,
      },
      {
        path: '/dashboard/settings/account',
        element: <YourAccount />,
      },
      {
        path: '/dashboard/organization',
        element: <Organization />,
      },
      {
        path: '/dashboard/groups',
        element: <Groups />,
      },
      {
        path: '/dashboard/groups/new',
        element: <AddGroup />,
      },
      {
        path: '/dashboard/groups/:groupID',
        element: <GroupDetails />,
      },
      {
        path: '/dashboard/users',
        element: <Users />,
      },
      {
        path: '/dashboard/users/new',
        element: <AddUser />,
      },
      {
        path: '/dashboard/users/:userID',
        element: <User />,
      },
      {
        path: '/dashboard/auditLog',
        element: <AuditLog />,
      },
      {
        path: '/dashboard/projects',
        element: <Projects />,
      },
      {
        path: '/dashboard/projects/new',
        element: <AddProject />,
      },
      {
        path: '/dashboard/projects/:businessID',
        element: <Project />,
      },
      {
        path: '/dashboard/projects/:businessID/contentitems',
        element: <ContentDatabaseItems />,
      },
      {
        path: '/dashboard/projects/:businessID/contentitems/new',
        element: <AddContentDatabaseItem />,
      },
      {
        path: '/dashboard/projects/:businessID/contentitems/:datamodelItemID',
        element: <ContentDatabaseItem />,
      },
      {
        path: '/dashboard/projects/:businessID/datamodels',
        element: <DataModels />,
      },
      {
        path: '/dashboard/projects/:businessID/datamodels/new',
        element: <AddDataModel />,
      },
      {
        path: '/dashboard/projects/:businessID/datamodels/:datamodelID',
        element: <DataModel />,
      },
      {
        path: '/dashboard/projects/:businessID/locations',
        element: <Locations />,
      },
      {
        path: '/dashboard/projects/:businessID/locations/:locationID',
        element: <Location />,
      },
      {
        path: '/dashboard/projects/:businessID/locations/:locationID/location_details',
        element: <LocationDetails />,
      },
      {
        path: '/dashboard/projects/:businessID/locations/:locationID/settings',
        element: <LocationSettings />,
      },
      {
        path: '/dashboard/projects/:businessID/locations/:locationID/social',
        element: <LocationSocial />,
      },
      {
        path: '/dashboard/projects/:businessID/settings',
        element: <ProjectSettings />,
      },
      {
        path: '/dashboard/projects/:businessID/formleads',
        element: <FormLeads />,
      },
      {
        path: '/dashboard/projects/:businessID/bulkedit',
        element: <BulkEdit />,
      },
      {
        path: '/dashboard/projects/:businessID/sites',
        element: <Websites />,
      },
      {
        path: '/dashboard/projects/:businessID/sites/new',
        element: <AddWebsite />,
      },
      {
        path: '/dashboard/projects/:businessID/templates',
        element: <Templates />,
      },
      {
        path: '/dashboard/projects/:businessID/templates/new',
        element: <AddTemplate />,
      },
      {
        path: '/dashboard/projects/:businessID/templates/:siteID',
        element: <Template />,
      },
      {
        path: '/dashboard/projects/:businessID/templates/:siteID/editor',
        element: <TemplateEditor />,
      },
      {
        path: '/dashboard/projects/:businessID/templates/:siteID/fallbacks',
        element: <TemplateFallbacks />,
      },
      {
        path: '/dashboard/projects/:businessID/templates/:siteID/blogs',
        element: <TemplateBlogs />,
      },
      {
        path: '/dashboard/projects/:businessID/templates/:siteID/blogs/posts/new',
        element: <TemplateAddPost />,
      },
      {
        path: '/dashboard/projects/:businessID/templates/:siteID/blogs/posts/:postID',
        element: <TemplatePost />,
      },
      {
        path: '/dashboard/projects/:businessID/templates/:siteID/menus',
        element: <TemplateMenus />,
      },
      {
        path: '/dashboard/projects/:businessID/templates/:siteID/menus/new',
        element: <AddMenu />,
      },
      {
        path: '/dashboard/projects/:businessID/templates/:siteID/menus/:linklistID',
        element: <TemplateMenu />,
      },
      {
        path: '/dashboard/projects/:businessID/templates/:siteID/pages/new',
        element: <TemplateAddPage />,
      },
      {
        path: '/dashboard/projects/:businessID/templates/:siteID/pages',
        element: <TemplatePages />,
      },
      {
        path: '/dashboard/projects/:businessID/templates/:siteID/pages/:pageID',
        element: <TemplatePage />,
      },
      {
        path: '/dashboard/projects/:businessID/templates/:siteID/settings',
        element: <TemplateSettings />,
      },
      {
        path: '/dashboard/search',
        element: <Search />,
      },
      {
        path: '/dashboard/sites/:siteID/access',
        element: <SiteAccess />,
      },
      {
        path: '/dashboard/sites/:siteID/blogs',
        element: <Blogs />,
      },
      {
        path: '/dashboard/sites/:siteID/blogs/authors',
        element: <Authors />,
      },
      {
        path: '/dashboard/sites/:siteID/blogs/authors/new',
        element: <AddAuthor />,
      },
      {
        path: '/dashboard/sites/:siteID/blogs/authors/:authorID',
        element: <Author />,
      },
      {
        path: '/dashboard/sites/:siteID/blogs/categories',
        element: <BlogCategories />,
      },
      {
        path: '/dashboard/sites/:siteID/blogs/categories/new',
        element: <AddBlogCategory />,
      },
      {
        path: '/dashboard/sites/:siteID/blogs/categories/:blogCategoryID',
        element: <BlogCategory />,
      },
      {
        path: '/dashboard/sites/:siteID/blogs/posts/new',
        element: <AddPost />,
      },
      {
        path: '/dashboard/sites/:siteID/blogs/posts/:postID',
        element: <Post />,
      },
      {
        path: '/dashboard/sites/:siteID/custom_fields',
        element: <SiteManageContent />,
      },
      {
        path: '/dashboard/sites/:siteID/location_details/:locationID',
        element: <SiteLocationDetails />,
      },
      {
        path: '/dashboard/sites/:siteID/location_details',
        element: <SiteLocationDetails />,
      },
      {
        path: '/dashboard/sites/:siteID',
        element: <InterstitialPage />,
      },
      {
        path: '/dashboard/sites/:siteID/pages',
        element: <Pages />,
      },
      {
        path: '/dashboard/sites/:siteID/pages/new',
        element: <AddPage />,
      },
      {
        path: '/dashboard/sites/:siteID/pages/:pageID',
        element: <SitePage />,
      },
      {
        path: '/dashboard/sites/:siteID/social',
        element: <SiteLocationSocial />,
      },
      {
        path: '/dashboard/sites/:siteID/settings',
        element: <SiteSettings />,
      },
      {
        path: '/dashboard/sites/:siteID/menus',
        element: <SiteMenus />,
      },
      {
        path: '/dashboard/sites/:siteID/menus/new',
        element: <SiteAddMenu />,
      },
      {
        path: '/dashboard/sites/:siteID/menus/:linklistID',
        element: <SiteMenu />,
      },
      {
        path: '/dashboard/sites/:siteID/SEO',
        element: <SEOPage />,
      },
      {
        path: '*',
        element: <Navigate to="/dashboard/projects" replace />,
      },
    ],
  },
]);

ReactDOM.createRoot(document.getElementById('root')).render(
  <QueryClientProvider client={queryClient}>
    <RouterProvider router={router} />
  </QueryClientProvider>,
);
